import type { ClipboardEvent, KeyboardEventHandler } from 'react';
import { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { mergeRefs } from 'react-merge-refs';
import type { NumberFormatValues, PatternFormatProps } from 'react-number-format';
import { PatternFormat } from 'react-number-format';

import { convertFormatStrToFormats, getNumberWithoutCountryCode, stripCountryCodeFromPhoneNumber } from './utils';

import { FormControl, FormItem, FormMessage } from '../../form';
import { Input } from '../input';
import type { InputProps } from '../type';

type TInputProps = Omit<PatternFormatProps<InputProps>, 'defaultValue' | 'value' | 'type'> & {
  formItemClassName?: string;
  value?: string | number | null;
  onValueChange: (v: string, formattedValue: string) => void;
  hideMsg?: boolean;
};

export const FormPatternInput = forwardRef<HTMLInputElement, TInputProps>(
  ({ formItemClassName, onValueChange, hideMsg = true, format = '', value, ...props }, ref) => {
    const getFormatDigitCount = useCallback((format: string) => {
      return format.replace(/[^#]/g, '').length;
    }, []);

    const { countryCode, formatsWithCountryCode, formatsWithoutCountryCode } = useMemo(() => {
      return convertFormatStrToFormats({ formatStr: format });
    }, [format]);

    const findMatchingFormatIndex = useCallback(
      (digitCount: number) => {
        for (let i = 0; i < formatsWithoutCountryCode.length; i++) {
          if (digitCount <= getFormatDigitCount(formatsWithoutCountryCode[i])) {
            return i;
          }
        }
        return formatsWithoutCountryCode.length - 1;
      },
      [formatsWithoutCountryCode, getFormatDigitCount]
    );

    const getDigitCount = useCallback(
      (value: string, withoutCountryCode = true) => {
        let processedValue = value;
        if (withoutCountryCode) {
          processedValue = getNumberWithoutCountryCode(value, countryCode);
        }
        return processedValue.replace(/\D/g, '').length;
      },
      [countryCode]
    );

    const [currentFormatIndex, setCurrentFormatIndex] = useState(
      findMatchingFormatIndex(getDigitCount(value?.toString() || ''))
    );
    const [isFormatTransition, setIsFormatTransition] = useState(false);
    const lastTypedDigit = useRef<string>('');
    const inputRef = useRef<HTMLInputElement>(null);

    const handleBackwardTransition = useCallback(
      (currentDigits: number) => {
        const nextDigits = currentDigits - 1;
        return findMatchingFormatIndex(nextDigits);
      },
      [findMatchingFormatIndex]
    );

    const handleForwardTransition = useCallback(
      (currentDigits: number, key: string) => {
        lastTypedDigit.current = '';
        const nextDigits = currentDigits + 1;
        if (nextDigits > getFormatDigitCount(formatsWithoutCountryCode[formatsWithoutCountryCode.length - 1])) {
          return findMatchingFormatIndex(currentDigits);
        }
        lastTypedDigit.current = key;
        setIsFormatTransition(true);
        return findMatchingFormatIndex(nextDigits);
      },
      [findMatchingFormatIndex, formatsWithoutCountryCode, getFormatDigitCount]
    );

    const handleKeyDown: KeyboardEventHandler<HTMLInputElement> = useCallback(
      (event) => {
        const currentDigits = getDigitCount(event.currentTarget.value);

        if (event.key === 'Backspace' || event.key === 'Delete') {
          const newFormatIndex = handleBackwardTransition(currentDigits);
          if (newFormatIndex !== currentFormatIndex) {
            setCurrentFormatIndex(newFormatIndex);
            event.preventDefault();
          }
        } else if (/^\d$/.test(event.key) || /^Numpad\d$/.test(event.code)) {
          const newFormatIndex = handleForwardTransition(currentDigits, event.key);
          if (newFormatIndex !== currentFormatIndex) {
            setCurrentFormatIndex(newFormatIndex);
            event.preventDefault();
          }
        }
      },
      [getDigitCount, handleBackwardTransition, handleForwardTransition, currentFormatIndex]
    );

    const handleChange = useCallback(
      (values: NumberFormatValues) => {
        if (onValueChange) {
          if (isFormatTransition) {
            // remove the country code and space
            const valueWithoutCode = getNumberWithoutCountryCode(values.value, countryCode);
            onValueChange(valueWithoutCode + lastTypedDigit.current, values.formattedValue);
            setIsFormatTransition(false);
            lastTypedDigit.current = '';
          } else {
            onValueChange(values.formattedValue, values.formattedValue);
          }
        }
      },
      [onValueChange, isFormatTransition, countryCode]
    );

    const handlePaste = useCallback(
      (event: ClipboardEvent<HTMLInputElement>) => {
        event.preventDefault();
        const pastedText = event.clipboardData.getData('text') || '';
        const textWithoutCountryCode = stripCountryCodeFromPhoneNumber(pastedText);
        console.log('PASTED TEXT', { pastedText, textWithoutCountryCode });

        const currentValue = event.currentTarget.value;

        // Get clean digits from pasted content
        const pastedDigits = textWithoutCountryCode.replace(/\D/g, '');
        const valueWithoutCode = getNumberWithoutCountryCode(currentValue, countryCode);
        const currentDigits = valueWithoutCode.replace(/\D/g, '');

        // Combine current and pasted digits
        const combinedDigits = currentDigits + pastedDigits;

        // Find appropriate format and limit to max allowed length
        const maxDigits = getFormatDigitCount(formatsWithoutCountryCode[formatsWithoutCountryCode.length - 1]);
        const limitedDigits = combinedDigits.slice(0, maxDigits);

        const newFormatIndex = findMatchingFormatIndex(limitedDigits.length);
        if (newFormatIndex !== currentFormatIndex) {
          setCurrentFormatIndex(newFormatIndex);
        }

        // Update value
        onValueChange(limitedDigits, limitedDigits);
      },
      [
        countryCode,
        getFormatDigitCount,
        formatsWithoutCountryCode,
        findMatchingFormatIndex,
        currentFormatIndex,
        onValueChange,
      ]
    );

    // Current format based on index
    const currentFormat = formatsWithCountryCode[currentFormatIndex] || formatsWithCountryCode[0] || '';
    const currentValue = getNumberWithoutCountryCode(value?.toString() || '', countryCode);

    useEffect(() => {
      if (isFormatTransition && inputRef.current) {
        const currentValueLength = currentFormat.length;
        inputRef.current.setSelectionRange(currentValueLength, currentValueLength);
        setIsFormatTransition(false);
      }
    }, [currentFormat, isFormatTransition]);

    return (
      <FormItem className={formItemClassName}>
        <FormControl>
          <PatternFormat
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            customInput={Input}
            {...props}
            value={currentValue}
            format={currentFormat}
            getInputRef={mergeRefs([ref, inputRef])}
            onValueChange={handleChange}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
          />
        </FormControl>
        {!hideMsg && <FormMessage />}
      </FormItem>
    );
  }
);
